import { debounce } from 'essential-utils';
import React from 'react';
import { useMounted } from '../../hooks';
const DEFAULT_COLUMN_WIDTH = 100;
export function useResize(apiRef, gridConfig) {
    const mounted = useMounted();
    React.useEffect(() => {
        const disposers = [];
        const isActionAllowed = () => {
            return mounted.current && !gridConfig?.isResetting;
        };
        function getAllColumns() {
            return apiRef.current?.getAllColumns() ?? [];
        }
        const originalColumns = getAllColumns().map((column) => ({
            ...column
        }));
        function calculateWidths() {
            if (isActionAllowed()) {
                apiRef.current?.updateColumns(getAllColumns()
                    .filter(({ flex }) => flex !== undefined)
                    .map((column) => {
                    const width = Math.floor(gridConfig?.wr.$?.columnWidths?.[column.field] ?? column.computedWidth);
                    return {
                        ...column,
                        // @see https://github.com/mui/mui-x/issues/4310#issuecomment-1082714528
                        flex: 0,
                        width
                    };
                }));
            }
        }
        function revertColumn(column) {
            const orgColumn = originalColumns.find(({ field }) => field === column.field);
            return {
                ...column,
                width: orgColumn?.width,
                flex: orgColumn?.flex
            };
        }
        const revertCalculatedWidths = debounce(() => {
            if (isActionAllowed()) {
                apiRef.current?.updateColumns(getAllColumns()
                    .filter(({ flex }) => flex === 0)
                    .map(revertColumn));
                calculateWidths();
            }
        }, 16);
        function initGridConfigInitialColumns() {
            if (!gridConfig) {
                return;
            }
            const allColumns = getAllColumns();
            gridConfig.updateInitialColumns(gridConfig.initialColumns.map((col) => {
                const { computedWidth } = allColumns.find((c) => c.field === col.field) ?? {};
                return {
                    ...col,
                    width: Math.floor(computedWidth ?? col.width ?? DEFAULT_COLUMN_WIDTH),
                    flex: 0
                };
            }));
        }
        function subscribeToFirstRowCountChange(callback) {
            const disposer = apiRef.current?.subscribeEvent('rowCountChange', debounce((count) => {
                if (count === 0 || !isActionAllowed()) {
                    return;
                }
                disposer?.();
                callback();
            }, 0));
            return disposer;
        }
        void (async () => {
            const config = await gridConfig?.wr.when();
            if (config?.columnWidths) {
                initGridConfigInitialColumns();
                calculateWidths();
            }
        })();
        disposers.push(subscribeToFirstRowCountChange(() => {
            if (gridConfig) {
                void (async () => {
                    const config = await gridConfig?.wr.when();
                    if (config?.columnWidths) {
                        initGridConfigInitialColumns();
                    }
                })();
            }
            else {
                initGridConfigInitialColumns();
            }
            calculateWidths();
            const recalculateWidths = () => {
                if (!gridConfig?.wr.$?.columnWidths) {
                    apiRef.current?.scrollToIndexes({ colIndex: 0 });
                    revertCalculatedWidths();
                }
            };
            const addWindowResizeListener = () => {
                window.addEventListener('resize', recalculateWidths);
                return () => {
                    window.removeEventListener('resize', recalculateWidths);
                };
            };
            disposers.push(addWindowResizeListener(), apiRef.current?.subscribeEvent('columnVisibilityModelChange', recalculateWidths));
        }));
        return () => {
            disposers.forEach((disposer) => disposer?.());
        };
    }, [apiRef, gridConfig, mounted]);
}
